import React, { useState, useEffect } from 'react';
import { useAutosave } from '../../../hooks/useAutosave';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Save, Plus, Trash2, Star, CheckCircle, Send, Mail, Clock, X, Copy, FileSignature, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AMLKYCForm = ({ caseId, aml, agent, onUpdate }) => {
  const [data, setData] = useState(aml || {});
  const [saving, setSaving] = useState(false);
  useAutosave(() => save(), data);

  useEffect(() => { setData(aml || {}); }, [aml]);

  const save = async () => {
    setSaving(true);
    try {
      await fetch(`${API_URL}/api/compliance/cases/${caseId}/aml-kyc?advisor_id=${agent.id}&advisor_name=${encodeURIComponent(agent.name)}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      onUpdate();
    } finally { setSaving(false); }
  };

  return (
    <Card data-testid="aml-kyc-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>AML / KYC Verification</CardTitle>
        <Button onClick={save} disabled={saving} className="bg-[#0077B6]" data-testid="save-aml-btn">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save'}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium mb-1">ID Type</label>
            <select value={data.id_type || ''} onChange={(e) => setData({ ...data, id_type: e.target.value })} className="w-full px-3 py-2 border rounded-lg" data-testid="aml-id-type">
              <option value="">Select...</option>
              <option value="passport">Passport</option>
              <option value="drivers_license">Driver's License</option>
              <option value="provincial_id">Provincial ID</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">ID Last 4 Digits</label>
            <input maxLength="4" value={data.id_last_four || ''} onChange={(e) => setData({ ...data, id_last_four: e.target.value })} className="w-full px-3 py-2 border rounded-lg" data-testid="aml-id-last4" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">ID Expiry Date</label>
            <input type="date" value={data.id_expiry_date || ''} onChange={(e) => setData({ ...data, id_expiry_date: e.target.value })} className="w-full px-3 py-2 border rounded-lg" data-testid="aml-id-expiry" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Verification Method</label>
            <select value={data.verification_method || ''} onChange={(e) => setData({ ...data, verification_method: e.target.value })} className="w-full px-3 py-2 border rounded-lg" data-testid="aml-verify-method">
              <option value="">Select...</option>
              <option value="in_person">In Person</option>
              <option value="video">Video</option>
              <option value="document">Document</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Source of Funds</label>
            <input value={data.source_of_funds || ''} onChange={(e) => setData({ ...data, source_of_funds: e.target.value })} className="w-full px-3 py-2 border rounded-lg" data-testid="aml-source-funds" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Payment Account Owner</label>
            <input value={data.payment_account_owner || ''} onChange={(e) => setData({ ...data, payment_account_owner: e.target.value })} className="w-full px-3 py-2 border rounded-lg" data-testid="aml-payment-owner" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Risk Rating</label>
            <select value={data.risk_rating || 'low'} onChange={(e) => setData({ ...data, risk_rating: e.target.value })} className="w-full px-3 py-2 border rounded-lg" data-testid="aml-risk">
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">PEP/HIO Result</label>
            <input value={data.pep_hio_result || ''} onChange={(e) => setData({ ...data, pep_hio_result: e.target.value })} className="w-full px-3 py-2 border rounded-lg" data-testid="aml-pep-result" />
          </div>
        </div>

        <div className="flex flex-col gap-2 pt-3 border-t">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={data.identity_verified || false} onChange={(e) => setData({ ...data, identity_verified: e.target.checked })} data-testid="aml-identity-verified" />
            <span className="text-sm">Identity Verified</span>
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={data.pep_hio_screening || false} onChange={(e) => setData({ ...data, pep_hio_screening: e.target.checked })} data-testid="aml-pep-screening" />
            <span className="text-sm">PEP/HIO Screening Completed</span>
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={data.third_party_determination || false} onChange={(e) => setData({ ...data, third_party_determination: e.target.checked })} data-testid="aml-third-party" />
            <span className="text-sm">Third Party Determination Made</span>
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={data.suspicious_transaction || false} onChange={(e) => setData({ ...data, suspicious_transaction: e.target.checked })} data-testid="aml-suspicious" />
            <span className="text-sm">Suspicious Transaction Reported</span>
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={data.completed || false} onChange={(e) => setData({ ...data, completed: e.target.checked })} data-testid="aml-completed" />
            <span className="text-sm font-semibold">Mark AML/KYC as Complete</span>
          </label>
        </div>
      </CardContent>
    </Card>
  );
};

// =============================================================
// Underwriting Notes
// =============================================================
